import { de } from '../../i18n/de';
import type { InputModeId } from '../input/InputAdapter';
import type { RepairLevel } from './useTapToSpeak';

/**
 * What the way back is called, in the words of the mode the person is actually in (`SUR-23`, ADR-047).
 *
 * ── Why a label needs a decision record ──
 *
 * ADR-046 made the repair control a registered target, which is what let the scan reach it at all. The cost
 * was C62's guard: in a timed mode a pointer click belongs to the adapter and is discarded. So
 * `Antippen zum Sprechen` went on naming a tap in the two modes where tapping had stopped working — the
 * instruction read by somebody with reduced acuity, seconds after a sentence they typed was not heard by
 * anyone, with nothing anywhere to suggest that a hold or a switch press was wanted instead.
 *
 * ── The bound on the precedent ──
 *
 * This is the only string in the app that varies by input mode, and ADR-047 says why it may: **a string may
 * vary by input mode only when it instructs a physical action.** Names, navigation, status and content never
 * do — a person who has learned what a screen is called must not find it renamed because a caregiver changed
 * a setting.
 *
 * The grouping below is `adapterFor`'s, deliberately and not by coincidence: `partnerAssisted` and
 * `hardwareKeyboard` resolve to the direct-touch adapter, nothing is discarded for them, and a tap is the
 * truth. A test enumerates all five so the two groupings cannot drift apart silently.
 */
export function repairLabel(level: RepairLevel, mode: InputModeId): string {
  // Named by no gesture, so there is nothing for the mode to change. `SHL-09`'s last resort reads the same
  // everywhere, which is also what keeps it recognisable as the step that costs something.
  if (level === 'reload') return de.audioReload;

  const retry = level === 'retry';

  switch (mode) {
    case 'dwell':
      return retry ? de.tapToSpeakDwell : de.enableAudioDwell;
    case 'scanning':
      return retry ? de.tapToSpeakScanning : de.enableAudioScanning;
    case 'directTouch':
    case 'partnerAssisted':
    case 'hardwareKeyboard':
      return retry ? de.tapToSpeak : de.enableAudio;
  }
}
